import { z } from "zod";

import { entityIdSchema, gitShaSchema, isoInstantSchema, localeSchema } from "./primitives";

/**
 * Publishing and deployment state (SPEC §23, §55, TASK-708).
 *
 * D1 records *what happened*, never *what the content is*. Losing these tables
 * costs history, not content (SPEC §6.1).
 */

/** Lifecycle of a publish attempt. */
export const publishJobStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
]);

export type PublishJobStatus = z.infer<typeof publishJobStatusSchema>;

export const publishJobSchema = z
  .object({
    id: entityIdSchema,

    siteId: entityIdSchema,
    entryId: entityIdSchema,
    locale: localeSchema,

    status: publishJobStatusSchema,

    /** Commit produced by a successful publish. */
    commitSha: gitShaSchema.optional(),
    /**
     * Sanitized failure reason. Internal detail is logged, never stored here
     * (AGENTS.md §21).
     */
    errorMessage: z.string().max(2000).optional(),

    createdAt: isoInstantSchema,
    startedAt: isoInstantSchema.optional(),
    finishedAt: isoInstantSchema.optional(),
  })
  .strict();

export type PublishJob = z.infer<typeof publishJobSchema>;

/** Body of `POST .../:locale/publish` (SPEC §56, AGENTS.md §23). */
export const publishRequestSchema = z
  .object({
    /**
     * SHA the draft was based on. When it no longer matches the repository head
     * the publish is rejected with `CONTENT_CONFLICT` (SPEC §25).
     */
    baseGithubSha: gitShaSchema.optional(),
    /** Override the commit message. Falls back to the canonical format. */
    commitMessage: z.string().min(1).max(200).optional(),
  })
  .strict();

export type PublishRequest = z.infer<typeof publishRequestSchema>;

/** Result of a successful publish, surfaced to the Admin UI (AGENTS.md §46). */
export const publishResultSchema = z.object({
  entryId: entityIdSchema,
  locale: localeSchema,
  commitSha: gitShaSchema,
  commitUrl: z.string().url(),
  /** Public URL of the published page, once known. */
  publicUrl: z.string().url().optional(),
  /** Deployment triggered by this publish, when one was triggered. */
  deploymentId: entityIdSchema.optional(),
});

export type PublishResult = z.infer<typeof publishResultSchema>;

/** Canonical commit message for a publish (AGENTS.md §16). */
export function buildPublishCommitMessage(title: string, locale: string): string {
  // Quotes inside the title would break the `content: publish "<title>"` shape.
  const safeTitle = title.replace(/"/g, "'").trim();
  return `content: publish "${safeTitle}" [${locale}]`;
}

// ---------------------------------------------------------------------------
// Deployments
// ---------------------------------------------------------------------------

export const deploymentStatusSchema = z.enum([
  "queued",
  "building",
  "success",
  "failed",
  "cancelled",
]);

export type DeploymentStatus = z.infer<typeof deploymentStatusSchema>;

export const deploymentSchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,

    status: deploymentStatusSchema,

    /** Commit the build ran against. */
    commitSha: gitShaSchema.optional(),
    /** Deployed site URL on success. */
    url: z.string().url().optional(),
    errorMessage: z.string().max(4000).optional(),

    createdAt: isoInstantSchema,
    startedAt: isoInstantSchema.optional(),
    finishedAt: isoInstantSchema.optional(),
  })
  .strict();

export type Deployment = z.infer<typeof deploymentSchema>;

/** A deployment is finished exactly when it has a terminal status. */
export function isDeploymentTerminal(status: DeploymentStatus): boolean {
  return status === "success" || status === "failed" || status === "cancelled";
}
